import { Badge, Button, Card } from '@safetyhub/ui';
import { LEGAL_POSITIONS } from '@safetyhub/shared';
import type { AdminCompanyRoleView } from '../../lib/company-rbac';

interface CompanyRoleTableProps {
  roles: AdminCompanyRoleView[];
  /** 전체 권한 키 수(권한 수 칸의 분모) */
  permissionTotal: number;
  /** 보관/복구 진행 중인 key */
  busyKey: string | null;
  onSelect: (role: AdminCompanyRoleView) => void;
  onArchive: (role: AdminCompanyRoleView) => void;
  onRestore: (role: AdminCompanyRoleView) => void;
}

/**
 * ① 시스템 역할 표 — sortOrder 순, 보관된 역할은 하단에 회색. 행 클릭 → 편집 모달. 보관/복구는 행 안 버튼(전파 방지).
 * 보관은 활성 배정이 있으면 서버가 409로 막으므로 버튼은 홀더 수와 무관하게 켜 두고 결과 문구를 토스트로 보여 준다
 * (홀더 수는 목록 조회 시점 값이라 잠그면 stale 위험).
 */
export default function CompanyRoleTable({
  roles,
  permissionTotal,
  busyKey,
  onSelect,
  onArchive,
  onRestore,
}: CompanyRoleTableProps) {
  const ordered = roles
    .slice()
    .sort((a, b) => {
      const aArchived = a.archivedAt ? 1 : 0;
      const bArchived = b.archivedAt ? 1 : 0;
      if (aArchived !== bArchived) return aArchived - bArchived;
      return a.sortOrder - b.sortOrder || a.key.localeCompare(b.key);
    });

  if (ordered.length === 0) {
    return (
      <Card padding="xl" radius="lg">
        <p className="text-center text-sm text-gray-500">시스템 역할이 없습니다.</p>
      </Card>
    );
  }

  return (
    <Card padding="none" radius="lg" className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm" data-testid="company-role-table">
        <thead className="bg-gray-50">
          <tr>
            {['이름', 'key', '법정 직위', '성질', '권한', '보유 조직', '오버라이드', '상태', ''].map((h, i) => (
              <th
                key={i}
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {ordered.map((role) => {
            const archived = role.archivedAt !== null;
            const legal = role.legalPosition ? LEGAL_POSITIONS[role.legalPosition] : null;
            const busy = busyKey === role.key;
            return (
              <tr
                key={role.key}
                data-role-key={role.key}
                data-archived={archived ? 'true' : undefined}
                className={`cursor-pointer hover:bg-gray-50 ${archived ? 'bg-gray-50 text-gray-400' : ''}`}
                onClick={() => onSelect(role)}
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className={`font-medium ${archived ? 'text-gray-500' : 'text-gray-900'}`}>{role.name}</div>
                  {role.description && <div className="max-w-xs truncate text-xs text-gray-400">{role.description}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">{role.key}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {legal ? (
                    <div>
                      <div className="text-gray-800">{legal.label}</div>
                      <div className="text-[11px] text-gray-400">{legal.article}</div>
                    </div>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    {role.isOwner && (
                      <Badge tone="rose" shape="rounded" size="sm">
                        owner
                      </Badge>
                    )}
                    {role.singleHolder && (
                      <Badge tone="amber" shape="rounded" size="sm">
                        1인
                      </Badge>
                    )}
                    {role.allowsNoAccount && (
                      <Badge tone="teal" shape="rounded" size="sm">
                        계정 불필요
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                  {role.permissionKeys.length}
                  <span className="text-gray-400"> / {permissionTotal}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                  {role.holderCompanyCount}
                  <span className="text-xs text-gray-400"> 조직 · {role.holderCount}명</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-700">{role.overrideCompanyCount} 조직</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {archived ? (
                    <Badge tone="slate" shape="pill" size="sm">
                      보관
                    </Badge>
                  ) : (
                    <Badge tone="emerald" shape="pill" size="sm">
                      활성
                    </Badge>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                  {role.isOwner ? null : archived ? (
                    <Button variant="secondary" size="xs" disabled={busy} onClick={() => onRestore(role)}>
                      {busy ? '처리 중' : '복구'}
                    </Button>
                  ) : (
                    <Button variant="secondary" size="xs" disabled={busy} onClick={() => onArchive(role)}>
                      {busy ? '처리 중' : '보관'}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
